import type { OAuth2StorageService } from '.';
import { injectable } from 'inversify';
import type { PkcePair } from 'src/features/oauth2/lib/pkce';
import { OAUTH_STATE_KEY, PKCE_PAIR_KEY } from 'src/lib/constants/storage-keys';
import { localforage } from 'src/lib/serialization/localForage';
import type { OAuthFlowState } from 'src/services/OAuth2Service';

/**
 * Web implementation of OAuth2StorageService that uses localforage for persistence
 * This is necessary for web because the OAuth flow involves a redirect,
 * so data must be persisted across page loads
 */
@injectable()
export class WebOAuth2StorageService implements OAuth2StorageService {
    async storePkcePair(pkcePair: PkcePair): Promise<void> {
        await localforage.setItem(PKCE_PAIR_KEY, pkcePair);
    }

    async getPkceVerifier(): Promise<string> {
        const pkce = await localforage.getItem<PkcePair>(PKCE_PAIR_KEY);
        if (!pkce) {
            throw new Error('PKCE pair not found in storage. OAuth flow may have been interrupted.');
        }
        return pkce.verifier;
    }

    async storeOAuthState(state: OAuthFlowState): Promise<void> {
        await localforage.setItem(OAUTH_STATE_KEY, state);
    }

    async getOAuthState(): Promise<OAuthFlowState> {
        return (await localforage.getItem<OAuthFlowState>(OAUTH_STATE_KEY)) ?? {};
    }

    async clearOAuthData(): Promise<void> {
        await Promise.all([localforage.removeItem(PKCE_PAIR_KEY), localforage.removeItem(OAUTH_STATE_KEY)]);
    }

    /**
     * For web, we always return false since the PKCE flow can't be interrupted
     * by multiple login attempts due to page redirects
     */
    isLoginFlowInProgress(): boolean {
        return false;
    }
}
